import getItem from 'owa-mail-store/lib/services/getItem';
import Item from 'owa-service/lib/contract/Item';
import ItemInfoResponseMessage from 'owa-service/lib/contract/ItemInfoResponseMessage';
import preserveItemDataInCache from 'owa-mail-store/lib/utils/preserveItemDataInCache';
import { action } from 'satcheljs/lib/legacy';
import { emailContextValues } from 'mini-mail-bootstrap/lib/routing/mailViewHandler';
import { generateItemId } from 'mini-mail-reading-pane-view/lib/utils/convertIDInEmailContext';
import { getBaseItemResponseShape } from 'owa-mail-store/lib/services/getItemResponseShape';
import { getStore } from 'owa-mail-store/lib/store/Store';
import { handleServerResponseSuccessAndError } from 'owa-service-utils/lib/handleServerResponseSuccessAndError';
import { LoadConversationItemActionSource } from 'owa-mail-store/lib/store/schema/LoadConversationItemActionSource';
import { MAIL_READING_PANE_LOAD_END } from 'mini-mail-workflow-tracking/lib/constants/ListViewWorkflowConstants';
import { signalWorkflowEvent } from 'mini-workflow-manager';
import { trace, TraceErrorObject } from 'owa-trace';

function parseItemResponse(responseItem: Item, itemId: string) {
    const mailStore = getStore();
    const cachedItem = mailStore.items.get(itemId);
    if (cachedItem) {
        // If the item already exists in the cache, make sure we preserve any data missing on the response item
        preserveItemDataInCache(responseItem, cachedItem);
    }

    mailStore.items.set(itemId, responseItem);
}

function compareItemIds(givenImmItemId: string, givenItemId: string, retrievedId: string) {
    if (
        givenImmItemId === retrievedId ||
        generateItemId(givenImmItemId) === retrievedId ||
        givenItemId === retrievedId ||
        generateItemId(givenItemId) === retrievedId
    ) {
        return true;
    }
    return false;
}

const parseItemResponseAction = action('parseItemResponse')(parseItemResponse);

/**
 * Load the data for a given item-id
 * @param itemIdToLoad
 * @param actionSource Indicates where the call was triggered from.
 * @param shapeName Indicates the shape of response which the consumer is expecting.
 *                  List of permissible shapeNames: https://o365exchange.visualstudio.com/O365%20Core/_git/Substrate?path=%2Fsources%2Fdev%2Fclients%2Fsrc%2FOwa2%2FServer%2FCore%2FWellKnownShapeName.cs&_a=contents&version=GBmaster
 *
 * Could be via prefetch or on opening reading pane
 */

export default function loadItem(
    itemIdToLoad: string,
    actionSource?: LoadConversationItemActionSource,
    shapeName?: string
): Promise<Item> {
    const mailStore = getStore();

    return getItem(itemIdToLoad, getBaseItemResponseShape(), shapeName || 'ItemNormalizedBody')
        .then(handleServerResponseSuccessAndError)
        .then(responseMessage => {
            const response = responseMessage as ItemInfoResponseMessage;
            parseItemResponseAction(response.Items[0], itemIdToLoad);
            emailContextValues.itemFound =
                response?.ResponseClass == 'Success' &&
                compareItemIds(
                    emailContextValues.immutableItemId,
                    emailContextValues.itemId,
                    itemIdToLoad
                )
                    ? true
                    : false;
            return mailStore.items.get(itemIdToLoad);
        })
        .catch((error: Error) => {
            switch (actionSource) {
                case LoadConversationItemActionSource.PrefetchFirstN:
                case LoadConversationItemActionSource.PrefetchAdjacentRowsOnDelay:
                case LoadConversationItemActionSource.PrefetchRowInCache:
                case LoadConversationItemActionSource.PrefetchSingleRow:
                    break;
                default:
                    if (error) {
                        error.message = `[GI] ${error.message}`;

                        // If the traceErrorObject returned from fetchHandler (refer owa-service -> fetchHandler) has the error.response value,
                        // the error message sometimes gets set to undefined due to internal handling of owa-analytics.
                        // For the time being we are setting the value to be null so that we don't get empty  error messages in the data-point.
                        // The error message set in the above line is already resourceful enough to convey what went wrong
                        // Remove once owa-analytics fixes this.
                        // (https://outlookweb.visualstudio.com/Outlook%20Web/_git/client-web?path=%2Fshared%2Finternal%2Fowa-analytics%2Fsrc%2Futils%2FaddErrorToDatapoint.ts&version=GBmaster&line=22&lineEnd=23&lineStartColumn=1&lineEndColumn=1&lineStyle=plain&_a=contents)
                        (error as TraceErrorObject).response = null;
                        trace.warn(error.message);
                    }

                    signalWorkflowEvent(
                        MAIL_READING_PANE_LOAD_END,
                        undefined,
                        { isCore: true },
                        { error }
                    );
                    break;
            }

            return null;
        });
}
